// Focus session audio cues + system notifications.
// Uses Web Audio API (no bundled asset). AudioContext must be unlocked
// from a user gesture (browsers block autoplay until then).

type AudioContextCtor = typeof AudioContext;

let ctx: AudioContext | null = null;
let unlocked = false;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor: AudioContextCtor | undefined =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) ctx = new Ctor();
  return ctx;
}

export function unlockAudio(): void {
  if (unlocked) return;
  const c = getCtx();
  if (!c) return;
  if (c.state === 'suspended') {
    c.resume().catch(() => {
      // ignore — will retry on next gesture
    });
  }
  unlocked = true;
}

function tone(freq: number, startOffset: number, duration: number, gain: number, type: OscillatorType = 'sine') {
  const c = getCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const env = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  const t0 = c.currentTime + startOffset;
  env.gain.setValueAtTime(0, t0);
  env.gain.linearRampToValueAtTime(gain, t0 + 0.02);
  env.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  osc.connect(env).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.05);
}

// Three-note ascending arpeggio: C5 → E5 → G5, ~900ms total.
// Soft attack, exponential release — gentle, non-jarring.
export function playEndChime(): void {
  const c = getCtx();
  if (!c || c.state === 'suspended') return;
  tone(523.25, 0.0, 0.45, 0.18);
  tone(659.25, 0.18, 0.45, 0.18);
  tone(783.99, 0.36, 0.7, 0.2);
}

// Single short blip — used when user manually ends the session.
export function playEndBlip(): void {
  const c = getCtx();
  if (!c || c.state === 'suspended') return;
  tone(440, 0.0, 0.25, 0.12, 'triangle');
}

export type NotificationPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

export function getNotificationPermission(): NotificationPermissionState {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission as NotificationPermissionState;
}

export async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission as NotificationPermissionState;
  }
  try {
    const result = await Notification.requestPermission();
    return result as NotificationPermissionState;
  } catch {
    return 'denied';
  }
}

export function notifySessionComplete(durationMin: number): void {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  try {
    const n = new Notification('Focus session complete', {
      body: `You focused for ${durationMin} minutes. Take a breath.`,
      tag: 'mindrift-focus',
      silent: false,
    });
    window.setTimeout(() => n.close(), 8000);
  } catch {
    // some browsers throw if not from a service worker — ignore
  }
}
